import React from 'react';
import type { useApplicationDetail } from '../../hooks/useApplicationDetail';
import type { ProductScoringConfig } from '../../../../shared/scoringTypes';

interface Props {
  h: ReturnType<typeof useApplicationDetail>;
  scoringConfig?: ProductScoringConfig | null;
}

export const VerificationSection: React.FC<Props> = ({ h, scoringConfig }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editedData = h.editedData as Record<string, any>;

  // Dynamic rendering when scoringConfig is provided and enabled
  const renderDynamicConfig = scoringConfig && scoringConfig.enabled;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-4 flex items-center">
        Weryfikacja i Preferencje
        <span className="ml-3 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Tryb edycji</span>
      </h2>
      <div className="space-y-6">
        {/* Status Wniosku - always visible */}
        <div>
          <h3 className="font-semibold mb-3">Status Wniosku</h3>
          <select value={editedData.status || ''} onChange={(e) => h.handleEditFieldChange('status', e.target.value)} className="w-full p-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 font-medium">
            <option value="new_application">Nowe zgłoszenie</option>
            <option value="initial_verification_ok">Wstępna Weryfikacja OK</option>
            <option value="warning_risk">Ostrzeżenie - Ryzyko</option>
            <option value="ready_to_write">Gotowy do pisania</option>
            <option value="contract_signed">Umowa podpisana</option>
            <option value="application_submitted">Wniosek złożony</option>
            <option value="success">Sukces</option>
            <option value="rejected">Odrzucony</option>
          </select>
        </div>

        {renderDynamicConfig ? (
          <>
            {/* Dynamic Eligibility Criteria */}
            {scoringConfig.eligibility.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Kryteria kwalifikacyjne</h3>
                <div className="space-y-2">
                  {scoringConfig.eligibility
                    .filter(c => c.operator === 'truthy' || c.operator === 'falsy')
                    .map(criterion => (
                      <label key={criterion.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editedData[criterion.field] || false}
                          onChange={(e) => h.handleEditFieldChange(criterion.field, e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm">{criterion.passMessage}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}

            {/* Status zatrudnienia - always visible */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status zatrudnienia</label>
              <select value={editedData.employment_status || ''} onChange={(e) => h.handleEditFieldChange('employment_status', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" aria-label="Status zatrudnienia">
                <option value="">Wybierz...</option>
                <option value="registered_unemployed">Zarejestrowany bezrobotny</option>
                <option value="unregistered">Niezarejestrowany</option>
                <option value="employed">Zatrudniony</option>
              </select>
            </div>

            {/* Dynamic BOOLEAN rules as checkboxes */}
            {scoringConfig.rules.filter(r => r.type === 'BOOLEAN').length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Punkty dodatkowe</h3>
                <div className="space-y-2">
                  {scoringConfig.rules
                    .filter(r => r.type === 'BOOLEAN')
                    .map(rule => (
                      <label key={rule.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editedData[rule.field] || false}
                          onChange={(e) => h.handleEditFieldChange(rule.field, e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm">{rule.label} {rule.booleanPoints ? `+${rule.booleanPoints} pkt` : ''}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}

            {/* Dynamic GROUP_SUM_CAPPED rules (preference groups) */}
            {scoringConfig.rules.filter(r => r.type === 'GROUP_SUM_CAPPED').map(rule => (
              <div key={rule.id}>
                <h3 className="font-semibold mb-3">{rule.label} {rule.cap ? `(max ${rule.cap} pkt)` : ''}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {(rule.groupFields || []).map(gf => (
                    <label key={gf.field} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editedData[gf.field] || false}
                        onChange={(e) => h.handleEditFieldChange(gf.field, e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">{gf.label} +{gf.points} pkt</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {/* Dynamic MATCH rules as select dropdowns */}
            {scoringConfig.rules.filter(r => r.type === 'MATCH' && r.options && r.options.length > 0).map(rule => (
              <div key={rule.id}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{rule.label}</label>
                <select
                  value={editedData[rule.field] || ''}
                  onChange={(e) => h.handleEditFieldChange(rule.field, e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  aria-label={rule.label}
                >
                  <option value="">Wybierz...</option>
                  {rule.options!.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} {opt.points > 0 ? `- +${opt.points} pkt` : `- ${opt.points} pkt`}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </>
        ) : (
          <>
            {/* Hardcoded PUP verification checkboxes (backward compat) */}
            <div>
              <h3 className="font-semibold mb-3">Status Weryfikacji</h3>
              <div className="space-y-2">
                {[
                  { field: 'has_ipd', label: 'Posiada IPD (Indywidualny Plan Działania)' },
                  { field: 'no_previous_grants', label: 'Brak wcześniejszych dotacji' },
                  { field: 'no_work_refusal', label: 'Brak odmowy pracy' },
                ].map(item => (
                  <label key={item.field} className="flex items-center">
                    <input type="checkbox" checked={editedData[item.field] || false} onChange={(e) => h.handleEditFieldChange(item.field, e.target.checked)} className="mr-2" />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status zatrudnienia</label>
              <select value={editedData.employment_status || ''} onChange={(e) => h.handleEditFieldChange('employment_status', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" aria-label="Status zatrudnienia">
                <option value="">Wybierz...</option>
                <option value="registered_unemployed">Zarejestrowany bezrobotny</option>
                <option value="unregistered">Niezarejestrowany</option>
                <option value="employed">Zatrudniony</option>
              </select>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Grupy Preferencyjne (Punkty)</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { field: 'is_dkr', label: 'Kobieta do 30 roku życia (DKR) +8 pkt' },
                  { field: 'is_over_50', label: 'Osoba powyżej 50 roku życia +5 pkt' },
                  { field: 'is_under_30', label: 'Osoba poniżej 30 roku życia +4 pkt' },
                  { field: 'is_disabled', label: 'Osoba niepełnosprawna +6 pkt' },
                  { field: 'is_long_term_unemployed', label: 'Długotrwale bezrobotny +3 pkt' },
                  { field: 'is_single_parent', label: 'Samotny rodzic +4 pkt' },
                  { field: 'has_no_qualifications', label: 'Bez kwalifikacji zawodowych +2 pkt' },
                  { field: 'is_location_in_krakow', label: 'Lokalizacja w Krakowie +2 pkt' },
                ].map(item => (
                  <label key={item.field} className="flex items-center">
                    <input type="checkbox" checked={editedData[item.field] || false} onChange={(e) => h.handleEditFieldChange(item.field, e.target.checked)} className="mr-2" />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Wykształcenie zgodne z branżą</label>
              <select value={editedData.is_education_related || 'nie'} onChange={(e) => h.handleEditFieldChange('is_education_related', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" aria-label="Wykształcenie zgodne z branżą">
                <option value="nie">Nie - 0 pkt</option>
                <option value="podobne">Podobne - +1 pkt</option>
                <option value="zgodne">Zgodne - +2 pkt</option>
              </select>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
